import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SortBy, SortOrder, Status as SupportStatus, SupportApiService } from '@vendasta/support';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  ReplaySubject,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { SupportTicketInfo } from './support-ticket-info';
import { CompanyAccountService } from '../../../company-account.service';
import { COMPANY_PROFILE_CARD_DATA_TOKEN } from '@galaxy/crm/static';
import { SupportTicketCardComponent } from './support-ticket-card/support-ticket-card.component';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatCardModule } from '@angular/material/card';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { CommonModule } from '@angular/common';
import { filter, skipWhile, takeUntil } from 'rxjs/operators';
import { MatChipListbox, MatChipListboxChange, MatChipOption } from '@angular/material/chips';

enum StatusEnum {
  OPEN = 'open',
  CLOSED = 'closed',
}

const CLOSED_STATUS = [SupportStatus.STATUS_CLOSED, SupportStatus.STATUS_SOLVED];
const OPEN_STATUS = [
  SupportStatus.STATUS_HOLD,
  SupportStatus.STATUS_NEW,
  SupportStatus.STATUS_OPEN,
  SupportStatus.STATUS_PENDING,
];
const ALL_STATUS = [...OPEN_STATUS, ...CLOSED_STATUS];
const PAGE = 5;

interface TicketList {
  tickets: SupportTicketInfo[];
  total: number;
  continue: boolean;
}

type Status = 'open' | 'closed' | 'all';

const companyPartnerFieldId = 'FieldID-6f2472c8-3401-4797-9db1-90fe524bd9a8';

@Component({
  selector: 'app-support-ticket-list',
  templateUrl: './list-support-ticket.component.html',
  styleUrls: ['./list-support-ticket.component.scss'],
  imports: [
    CommonModule,
    GalaxyPipesModule,
    MatIconModule,
    TranslateModule,
    GalaxyFormFieldModule,
    MatCardModule,
    GalaxyLoadingSpinnerModule,
    GalaxyAlertModule,
    SupportTicketCardComponent,
    MatChipListbox,
    MatChipOption,
  ],
})
export class SupportTicketListComponent implements OnInit, OnDestroy {
  private readonly data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);

  @Input() set partner(partnerId: string) {
    this.partnerId$$.next(partnerId);
  }
  @Input() title: string;
  @Input() status: Status;
  @Output() ticketsCount = new EventEmitter<number>();

  private partnerId$$ = new ReplaySubject<string>(1);
  private companyPartnerId$$ = new ReplaySubject<string>(1);
  selectedStatus: Status = 'all';

  private readonly isLoading$$ = new BehaviorSubject<boolean>(true);
  protected readonly isLoading$ = this.isLoading$$.asObservable();

  private showMore$$ = new BehaviorSubject<number>(PAGE);
  private readonly destroy$$ = new Subject<void>();

  private readonly supportService = inject(SupportApiService);
  private readonly accountCreationService = inject(CompanyAccountService);

  readonly tickets$ = combineLatest([this.companyPartnerId$$, this.showMore$$]).pipe(
    skipWhile(([pid]) => !pid),
    tap(() => this.isLoading$$.next(true)),
    switchMap(([pid, page]) =>
      this.fetchTickets(pid, this.selectedStatus).pipe(
        map((res) => ({
          tickets: res.tickets,
          total: res.total,
          continue: page < res.total,
        })),
      ),
    ),
    startWith({ tickets: [], total: 0, continue: false } as TicketList),
    tap(() => this.isLoading$$.next(false)),
    takeUntil(this.destroy$$),
  );

  pagedTickets$: Observable<TicketList> = combineLatest([this.tickets$, this.showMore$$]).pipe(
    map(([tickets, page]) => ({
      tickets: tickets.tickets.slice(0, page),
      total: tickets.total,
      continue: page < tickets.total,
    })),
  );

  ngOnInit() {
    this.loadTicketsByPartnerId();

    this.tickets$.pipe(takeUntil(this.destroy$$)).subscribe((ticketList) => {
      if (ticketList) {
        this.ticketsCount.emit(ticketList.total);
      }
    });
  }

  private loadTicketsByPartnerId(): void {
    const company$ = this.accountCreationService.getCompany$(this.data.company_id);
    company$
      .pipe(
        map((company) => company.fields?.find((field) => field.fieldId === companyPartnerFieldId)?.stringValue),
        filter((pid) => !!pid),
      )
      .subscribe((pid) => {
        this.companyPartnerId$$.next(pid);
      });
  }

  private fetchTickets(partnerId: string, status: Status): Observable<TicketList> {
    const statuses = status === 'all' ? ALL_STATUS : status === StatusEnum.OPEN ? OPEN_STATUS : CLOSED_STATUS;

    return this.supportService
      .searchTickets({
        partnerId,
        searchTerm: '',
        sortBy: status === StatusEnum.OPEN ? SortBy.SORT_BY_CREATED_AT : SortBy.SORT_BY_UPDATED_AT,
        sortOrder: SortOrder.SORT_ORDER_DESC,
        statuses,
      })
      .pipe(
        map((res) => ({
          tickets: res?.tickets?.map((ticket) => new SupportTicketInfo(ticket)) || [],
          total: res?.count || 0,
          continue: res?.tickets?.length > PAGE,
        })),
      );
  }

  onStatusChange(event: MatChipListboxChange): void {
    this.selectedStatus = event.value;
    this.showMore$$.next(PAGE);
    this.loadTicketsByPartnerId();
  }

  ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  view5More(): void {
    this.showMore$$.next(this.showMore$$.value + PAGE);
  }
}
